'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { ComponentProps } from 'react';
import React from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { GoArrowRight } from 'react-icons/go';
import { BiCalendar } from 'react-icons/bi';
import { HtmlDescription } from '../homepage/shared/html-description';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { Card } from '@eliadmin/core/src/components/ui/card';

export type BlogCardProps = Omit<ComponentProps<typeof Card>, 'className'> & {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
};

export function BlogCard({ blog, className, ...props }: BlogCardProps) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  const textContent = getTextFromHTML(blog.content);

  if (blog.image?.url)
    return (
      <div
        key={blog.slug}
        className="col-span-1 flex w-full flex-col items-end justify-start gap-6 lg:gap-8"
      >
        <Link
          prefetch={false}
          href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
          className="relative aspect-video w-full"
        >
          <Image
            src={blog?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(blog?.image, blog?.name)}
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            sizes="(min-width: 768px) 50vw, 100vw"
          />
        </Link>
        <Link
          prefetch={false}
          href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
          className="flex w-full items-start justify-start"
        >
          <h3
            data-ea-visual-tag="h2"
            className={cn(
              'line-clamp-2 h-[56px] lg:h-[78px]',
              'w-full text-left text-xl font-bold text-foreground md:text-2xl lg:text-3xl',
            )}
          >
            {blog.name}
          </h3>
        </Link>
        <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}>
          <HtmlDescription
            content={textContent}
            className="line-clamp-2 w-full md:line-clamp-3"
            color="gray"
            size="large"
          />
        </Link>

        <div className="flex w-full items-center justify-between">
          <div className="flex items-center justify-start gap-1.5 text-base font-normal text-[#8C8C8C] lg:text-lg">
            <BiCalendar size={24} />
            {new Date(blog.created_at).toLocaleDateString(locale)}
          </div>
          <Button asChild variant={'outline'} className="gap-2">
            <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}>
              {t('text1148')} <GoArrowRight size={20} />
            </Link>
          </Button>
        </div>
      </div>
    );
}
